import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, AlertCircle, Info, AlertTriangle, Bug, Download, Clock } from 'lucide-react';
import { logsAPI } from '../services/api';
import { sessionsAPI } from '../services/sessionsAPI';

function Logs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionFilter, setSessionFilter] = useState('');
  const [limit, setLimit] = useState(200);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterLevel, setFilterLevel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  const fetchSessions = async () => {
    try {
      const res = await sessionsAPI.getAll();
      setSessions(res.data.sessions || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      const sid = sessionFilter || null;
      const [logRes, sumRes] = await Promise.all([
        logsAPI.getAll(sid, limit),
        logsAPI.getSummary(sid),
      ]);
      setLogs(logRes.data.logs || []);
      setSummary(sumRes.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch logs');
      console.error('Error fetching logs:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionFilter, limit]);

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLogs, 8000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchLogs]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ exported_at: new Date().toISOString(), logs: filteredLogs }, null, 2)], {
      type: 'application/json',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bot-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const filteredLogs = logs.filter((log) => {
    const q = searchTerm.toLowerCase();
    const msg = (log.message || '').toLowerCase();
    const pid = (log.project_id || '').toLowerCase();
    const sid = (log.session_id || '').toLowerCase();
    const matchesSearch = !q || msg.includes(q) || pid.includes(q) || sid.includes(q);
    const matchesFilter = filterLevel === 'all' || (log.level || '').toLowerCase() === filterLevel;
    return matchesSearch && matchesFilter;
  });

  const getLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-danger-400" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-warning-400" />;
      case 'info':
        return <Info className="w-4 h-4 text-primary-400" />;
      case 'debug':
        return <Bug className="w-4 h-4 text-slate-400" />;
      default:
        return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return 'bg-danger-900/40 text-danger-200 border-danger-800/50';
      case 'warning':
        return 'bg-warning-900/40 text-warning-200 border-warning-800/50';
      case 'info':
        return 'bg-primary-900/40 text-primary-200 border-primary-800/50';
      case 'debug':
        return 'bg-slate-800 text-slate-200 border-slate-600';
      default:
        return 'bg-slate-800 text-slate-200 border-slate-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && logs.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-slate-100">Error</h3>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <button onClick={() => fetchLogs()} className="mt-4 btn btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-100">Logs</h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setIsLoading(true);
              fetchLogs();
            }}
            disabled={isLoading}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={exportJson}
            disabled={filteredLogs.length === 0}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export JSON</span>
          </button>
        </div>
      </div>

      {summary && (
        <div className="card flex flex-wrap items-center gap-4 text-sm">
          <span className="font-medium text-slate-300">Loaded batch: {logs.length} rows</span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">
            DB totals{sessionFilter ? ' (this session)' : ''}: {summary.total} —{' '}
            {Object.entries(summary.by_level || {})
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ') || 'no breakdown'}
          </span>
        </div>
      )}

      <div className="card space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
          <div>
            <label className="label">Search message / project / session</label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">Level</label>
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="input"
            >
              <option value="all">All Levels</option>
              <option value="error">Error</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>
          </div>
          <div>
            <label className="label">Session filter</label>
            <select
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
              className="input"
            >
              <option value="">All sessions</option>
              {sessions.map((s) => (
                <option key={s.session_id} value={s.session_id}>
                  {s.name || s.session_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Fetch limit</label>
            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="input">
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded border-slate-600"
          />
          <Clock className="w-4 h-4 text-slate-500" />
          Auto-refresh every 8s
        </label>
      </div>

      <div className="space-y-2">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium text-slate-100">No logs found</h3>
            <p className="mt-1 text-sm text-slate-500">
              {searchTerm || filterLevel !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'No logs have been generated yet.'}
            </p>
          </div>
        ) : (
          filteredLogs.map((log) => (
            <div key={log.id} className="card">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">{getLevelIcon(log.level)}</div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getLevelColor(log.level)}`}
                      >
                        {log.level?.toUpperCase() || 'UNKNOWN'}
                      </span>
                      {log.session_id && (
                        <span className="text-xs text-slate-500 font-mono" title={log.session_id}>
                          Session: {log.session_id.slice(0, 8)}…
                        </span>
                      )}
                      {log.project_id && (
                        <span className="text-xs text-slate-500">Project: {log.project_id}</span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">{formatDate(log.timestamp)}</span>
                  </div>

                  <pre className="text-sm text-slate-100 whitespace-pre-wrap break-words font-sans">
                    {log.message}
                  </pre>

                  {log.additional_data && (
                    <details className="mt-2">
                      <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-300">
                        Additional data (API errors, bid metadata)
                      </summary>
                      <pre className="mt-1 text-xs text-slate-400 bg-slate-950/80 p-2 rounded border border-slate-700/80 overflow-x-auto max-h-64 overflow-y-auto">
                        {JSON.stringify(log.additional_data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Logs;
